import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Loader2,
  ShieldCheck,
  Wallet,
  TrendingUp,
  ArrowLeftRight,
  BarChart3,
  Lock,
} from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uyaniyor, setUyaniyor] = useState(null); // {deneme, toplam} — sunucu uyanma durumu

  // Önizleme (geliştirme) ortamında mıyız? Canlı sitede bu uyarı görünmez.
  const onizlemedeMi = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent");
  // Canlı site adresi ortam değişkeninden okunur; tanımsızsa mevcut adres kullanılır.
  const CANLI_ADRES = process.env.REACT_APP_CANLI_ADRES || window.location.origin;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Lütfen e-posta ve şifrenizi girin.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const status = err?.response?.status;
      // Sunucu soğuk başlangıçta (uyku sonrası) 1-2 dakika yanıt veremeyebilir:
      // ağ hatası veya 502/503/504 durumlarında otomatik olarak tekrar dene.
      if (!err?.response || [502, 503, 504].includes(status)) {
        const TOPLAM = 20; // 20 deneme × 5 sn ≈ 100 sn bekleme penceresi
        toast.info("Sunucu uyanıyor — şifreniz doğru olsa bile ilk 1-2 dakika bekletebilir. Otomatik deneniyor, lütfen sayfadan ayrılmayın…", { duration: 10000 });
        let basarili = false;
        for (let i = 0; i < TOPLAM; i++) {
          setUyaniyor({ deneme: i + 1, toplam: TOPLAM });
          await new Promise((r) => setTimeout(r, 5000));
          try {
            await login(email, password);
            basarili = true;
            break;
          } catch (err2) {
            const s2 = err2?.response?.status;
            if (err2?.response && ![502, 503, 504].includes(s2)) {
              // Sunucu uyandı ama giriş reddedildi (örn. yanlış şifre)
              setUyaniyor(null);
              toast.error(err2?.response?.data?.detail || "Giriş yapılamadı. Lütfen tekrar deneyin.");
              setSubmitting(false);
              return;
            }
          }
        }
        setUyaniyor(null);
        if (basarili) {
          toast.success("Sunucu hazır — giriş yapıldı.");
          navigate("/");
        } else {
          toast.error("Sunucuya ulaşılamıyor. Lütfen sayfayı yenileyip tekrar deneyin.");
        }
      } else {
        const msg = err?.response?.data?.detail || "Giriş yapılamadı. Lütfen tekrar deneyin.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const MODULLER = [
    { icon: TrendingUp, baslik: "Ciro & Tahsilat", aciklama: "Fatura, vade ve tahsilat takibi" },
    { icon: Wallet, baslik: "Gider & Bütçe", aciklama: "48 kalem canlı bütçe yönetimi" },
    { icon: ArrowLeftRight, baslik: "Nakit Akış & Kasa", aciklama: "Günlük, haftalık ve aylık akış" },
    { icon: BarChart3, baslik: "Finansal Analiz", aciklama: "AI destekli raporlar ve özetler" },
  ];

  return (
    <div className="relative min-h-screen bg-[hsl(var(--primary))] font-body flex items-center justify-center px-4 py-8 overflow-hidden">
      {/* Arka plan dokusu — kurumsal mavi üzerinde hafif ızgara */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[720px] -translate-x-1/2 rounded-full opacity-70"
        style={{ background: "radial-gradient(closest-side, rgba(226,178,60,0.18), transparent)" }}
      />

      <div className="relative w-full max-w-5xl">
        {/* ÖNİZLEME UYARISI — yalnızca geliştirme ortamında görünür */}
        {onizlemedeMi && (
          <div
            className="mb-4 flex flex-wrap items-center justify-center gap-2 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-4 py-3 text-center"
            data-testid="onizleme-uyari-banner"
          >
            <p className="text-sm text-white">
              <strong className="text-[hsl(var(--accent))]">Burası test ortamıdır ve zaman zaman uykuya geçer.</strong>{" "}
              Günlük kullanım için her zaman açık olan canlı adresi kullanın:
            </p>
            <a
              href={CANLI_ADRES}
              className="inline-flex items-center rounded-lg bg-[hsl(var(--accent))] px-3.5 py-1.5 text-sm font-semibold text-[hsl(var(--primary))] transition-transform duration-150 hover:-translate-y-0.5"
              data-testid="canli-adres-link"
            >
              Canlı Siteye Git →
            </a>
          </div>
        )}

        {/* Üst marka şeridi */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="mb-6 flex items-center justify-center gap-3"
          data-testid="login-brand-header"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-[hsl(var(--accent))]/50 font-display text-lg font-bold text-[hsl(var(--accent))] shadow-md shadow-black/20">
            3A
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-white">
              3A-FYS <span className="font-medium text-white/70">– Finans Yönetim Sistemi</span>
            </h1>
            <p className="text-xs text-white/55">3A Otomotiv • Kurumsal Finans Platformu</p>
          </div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr] items-stretch">
          {/* Giriş kartı */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.05 }}
          >
            <div className="flex h-full flex-col justify-center rounded-2xl border border-white/10 bg-white p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.28)]">
              <div className="mb-6 flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/5">
                  <Lock className="h-4 w-4 text-[hsl(var(--primary))]" />
                </span>
                <div>
                  <h2 className="font-display text-lg font-semibold leading-tight">Güvenli Giriş</h2>
                  <p className="text-xs text-muted-foreground">Hesabınıza erişmek için bilgilerinizi girin.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="ornek@3afinans.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 text-base rounded-lg bg-white"
                    data-testid="login-form-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Şifre</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 text-base rounded-lg bg-white"
                    data-testid="login-form-password-input"
                  />
                </div>
                {uyaniyor && (
                  <div
                    className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3 text-amber-900"
                    data-testid="login-sunucu-uyaniyor-banner"
                  >
                    <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold">Sunucu uyanıyor, lütfen bekleyin…</p>
                      <p>Şifrenizle ilgili bir sorun yok. Uygulama bir süre kullanılmayınca sunucu uykuya geçer; ilk açılış 1-2 dakika sürebilir. Giriş otomatik tamamlanacak. (Deneme {uyaniyor.deneme}/{uyaniyor.toplam})</p>
                    </div>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-12 w-full rounded-lg text-base font-medium transition-colors duration-150 active:scale-[0.98]"
                  data-testid="login-form-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {uyaniyor ? "Sunucu uyanıyor, bekleyin…" : "Giriş yapılıyor…"}
                    </>
                  ) : (
                    "Giriş Yap"
                  )}
                </Button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-1.5 border-t border-slate-100 pt-4 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                Rol bazlı yetkilendirme ve KVKK uyumlu veri koruması
              </div>
            </div>
          </motion.div>

          {/* Tanıtım paneli — açık tema */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, delay: 0.12 }}
            className="hidden lg:flex flex-col justify-between rounded-2xl border border-white/10 bg-white/95 backdrop-blur-sm p-8 shadow-[0_20px_50px_rgba(0,0,0,0.24)]"
            data-testid="login-tanitim-paneli"
          >
            <div>
              <p className="font-display text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]/60">
                3A Otomotiv • İstanbul
              </p>
              <h2 className="mt-3 font-display text-[26px] font-bold leading-snug text-[hsl(var(--primary))]">
                Finansal yönetiminiz tek panelde.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Ciro, gider, maliyet, kasa ve personel yönetimi — kaporta, boya,
                mekanik ve elektrik servisiniz için tasarlandı.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-3">
              {MODULLER.map((m) => (
                <div
                  key={m.baslik}
                  className="flex items-center gap-3.5 rounded-xl border border-slate-200/80 bg-white px-4 py-3 transition-transform duration-150 hover:-translate-y-0.5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/5">
                    <m.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{m.baslik}</p>
                    <p className="text-xs text-muted-foreground">{m.aciklama}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-8 text-[11px] text-muted-foreground/70">
              © {new Date().getFullYear()} 3A Otomotiv — 3A-FYS Finans Yönetim Sistemi
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
